import type { OfflineOperation, OutboxScope, OutboxStore, OutboxSummary } from "./types";

const DEFAULT_DB_NAME = "discus-ai-v2";
const DB_VERSION = 1;
const STORE_NAME = "sync_outbox";

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("INDEXED_DB_REQUEST_FAILED"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("INDEXED_DB_TRANSACTION_FAILED"));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("INDEXED_DB_TRANSACTION_ABORTED"));
  });
}

function copyOperation(operation: OfflineOperation): OfflineOperation {
  return structuredClone(operation);
}

function matchesScope(operation: OfflineOperation, scope?: OutboxScope): boolean {
  return !scope || (operation.farmId === scope.farmId && operation.actorId === scope.actorId);
}

function isActive(operation: OfflineOperation): boolean {
  return (
    operation.status === "pending" ||
    operation.status === "syncing" ||
    operation.status === "retryable_error" ||
    operation.status === "terminal_conflict"
  );
}

export class IndexedDbOutboxStore implements OutboxStore {
  private databasePromise: Promise<IDBDatabase> | undefined;

  constructor(private readonly databaseName = DEFAULT_DB_NAME) {}

  private open(): Promise<IDBDatabase> {
    if (this.databasePromise) {
      return this.databasePromise;
    }

    this.databasePromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.databaseName, DB_VERSION);

      request.onupgradeneeded = () => {
        const database = request.result;

        if (!database.objectStoreNames.contains(STORE_NAME)) {
          const store = database.createObjectStore(STORE_NAME, {
            keyPath: "operationId",
          });
          store.createIndex("createdAt", "createdAt", { unique: false });
          store.createIndex("status", "status", { unique: false });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        this.databasePromise = undefined;
        reject(request.error ?? new Error("INDEXED_DB_OPEN_FAILED"));
      };
      request.onblocked = () => {
        this.databasePromise = undefined;
        reject(new Error("INDEXED_DB_OPEN_BLOCKED"));
      };
    });

    return this.databasePromise;
  }

  async put(operation: OfflineOperation): Promise<void> {
    const database = await this.open();
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put(copyOperation(operation));
    await transactionDone(transaction);
  }

  async get(operationId: string): Promise<OfflineOperation | undefined> {
    const database = await this.open();
    const transaction = database.transaction(STORE_NAME, "readonly");
    const result = await requestResult(
      transaction.objectStore(STORE_NAME).get(operationId) as IDBRequest<
        OfflineOperation | undefined
      >,
    );
    await transactionDone(transaction);
    return result ? copyOperation(result) : undefined;
  }

  async listReady(nowIso: string, limit = 50, scope?: OutboxScope): Promise<OfflineOperation[]> {
    const database = await this.open();
    const transaction = database.transaction(STORE_NAME, "readonly");
    const all = await requestResult(
      transaction.objectStore(STORE_NAME).getAll() as IDBRequest<OfflineOperation[]>,
    );
    await transactionDone(transaction);

    return all
      .filter(
        (operation) =>
          matchesScope(operation, scope) &&
          (operation.status === "pending" ||
            (operation.status === "retryable_error" &&
              (!operation.nextRetryAt || operation.nextRetryAt <= nowIso))),
      )
      .sort((left, right) => left.createdAt.localeCompare(right.createdAt))
      .slice(0, limit)
      .map(copyOperation);
  }

  async listActive(scope: OutboxScope, limit = 100): Promise<OfflineOperation[]> {
    const database = await this.open();
    const transaction = database.transaction(STORE_NAME, "readonly");
    const all = await requestResult(
      transaction.objectStore(STORE_NAME).getAll() as IDBRequest<OfflineOperation[]>,
    );
    await transactionDone(transaction);

    return all
      .filter((operation) => matchesScope(operation, scope) && isActive(operation))
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
      .slice(0, limit)
      .map(copyOperation);
  }

  async summarize(scope?: OutboxScope): Promise<OutboxSummary> {
    const database = await this.open();
    const transaction = database.transaction(STORE_NAME, "readonly");
    const all = await requestResult(
      transaction.objectStore(STORE_NAME).getAll() as IDBRequest<OfflineOperation[]>,
    );
    await transactionDone(transaction);

    return all.reduce<OutboxSummary>(
      (summary, operation) => {
        if (!matchesScope(operation, scope)) return summary;
        if (operation.status === "pending") summary.pending += 1;
        if (operation.status === "syncing") summary.syncing += 1;
        if (operation.status === "retryable_error") summary.retryable += 1;
        if (operation.status === "terminal_conflict") summary.conflicts += 1;
        return summary;
      },
      { pending: 0, syncing: 0, retryable: 0, conflicts: 0 },
    );
  }

  async update(operationId: string, patch: Partial<OfflineOperation>): Promise<void> {
    const database = await this.open();
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const existing = await requestResult(
      store.get(operationId) as IDBRequest<OfflineOperation | undefined>,
    );

    if (!existing) {
      transaction.abort();
      throw new Error("SYNC_OPERATION_NOT_FOUND");
    }

    store.put(copyOperation({ ...existing, ...patch }));
    await transactionDone(transaction);
  }

  async resetInterrupted(scope?: OutboxScope): Promise<number> {
    const database = await this.open();
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const all = await requestResult(store.getAll() as IDBRequest<OfflineOperation[]>);
    let resetCount = 0;

    for (const operation of all) {
      if (operation.status === "syncing" && matchesScope(operation, scope)) {
        store.put({
          ...operation,
          status: "pending",
          lastErrorCode: "SYNC_INTERRUPTED",
        } satisfies OfflineOperation);
        resetCount += 1;
      }
    }

    await transactionDone(transaction);
    return resetCount;
  }

  async close(): Promise<void> {
    if (!this.databasePromise) {
      return;
    }

    const database = await this.databasePromise;
    database.close();
    this.databasePromise = undefined;
  }
}
